import { ref, update } from "firebase/database";
import { database } from "./firebase";
import { nativeUpdate, shouldUseNativeDatabase } from "./firebase-native-database";

/**
 * Lightweight, fire-and-forget page journey logger for signed-in users.
 *
 * Writes versioned events to the existing /journeys day/session buckets.
 * Events are queued in memory and flushed in a single multi-path update every
 * few seconds after actual events (or immediately when backgrounded). There is
 * no periodic heartbeat. Retention is handled server-side.
 */

export const JOURNEY_EXIT_PAGE = "__exit";
const SESSION_STORAGE_KEY = "cr_journey_session";
const SESSION_IDLE_MS = 30 * 60 * 1000;
const FLUSH_DELAY_MS = 5000;
const MAX_QUEUE = 20;
export const ENGAGEMENT_WINDOW_MS = 15_000;
const MAX_TIMING_GAP_MS = 30_000;

type EventKind = "page" | "visible" | "interaction" | "action" | "end" | "hidden" | "exit" | "rendered";
export interface JourneyRouteContext {
  route?: string;
  previousRoute?: string;
  source?: "redirect" | "user" | "history" | "entry" | "auth_state" | "unknown";
  navigationType?: "POP" | "PUSH" | "REPLACE";
  redirect?: string;
}
type Interaction = "click" | "submit" | "scroll" | "keyboard" | "edit";
interface EventData {
  p: string;
  t: number;
  tm?: string;
  v: 2;
  k: EventKind;
  i: string;
  q: number;
  d: number;
  a: number;
  n?: string;
  u?: boolean;
  rt?: string;
  pr?: string;
  s?: JourneyRouteContext["source"];
  nt?: JourneyRouteContext["navigationType"];
  rr?: string;
  o?: "success" | "failure";
}

interface QueuedEvent extends EventData {
  uid: string;
  day: string;
  sessionId: string;
  eventId: string;
}

let queue: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let lastPage: { uid: string; page: string; team?: string; visitId: string; route?: JourneyRouteContext; rendered?: boolean; renderedTeam?: string } | null = null;
let session: { uid: string; id: string; day: string; lastActivity: number; sequence: number } | null = null;
let visible = true;
let visitOpen = false;
let lastTick = 0;
let lastWall = 0;
let activeUntil = 0;
let lastScroll = -Infinity;
let eventCounter = 0;
let lastNavigationSignal = -Infinity;
// Set after a permission denial (e.g. rules not deployed) so we stop retrying until reload.
let disabled = false;

const isPermissionDenied = (error: unknown) => {
  const text = `${(error as { code?: string })?.code ?? ""} ${error instanceof Error ? error.message : String(error)}`;
  // Web SDK: PERMISSION_DENIED; native REST: "Failed to update data: 401".
  return /permission[_ ]denied|: 40[13]\b/i.test(text);
};

const randomId = (length: number) => {
  const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
  let out = "";
  for (let i = 0; i < length; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
};

const sanitise = (value: string, max: number) =>
  value.toLowerCase().replace(/[^a-z0-9_\-/:]/g, "").slice(0, max);
const TEAM_SECTIONS = new Set([
  "home", "players", "results", "info", "stats", "lookup", "blog", "fixtures", "news",
  "settings", "followrequests", "supporters", "options", "detailsettings", "teambuilder",
  "playertimers", "tournamenttracker", "gameplanner", "chat", "payments", "tacticalvideos",
  "tacticsboard", "trainingplans", "playerintel", "spotlight", "roles", "ai",
]);
const CLUB_SECTIONS = new Set(["home", "fixtures", "results", "teams", "blog", "links", "about", "news", "payments", "bookings", "scouts", "settings", "pitches"]);
const DIRECTORY_SECTIONS = new Set(["welcome", "list", "clubs", "signup", "tools", "guidedhelp", "audit", "scouts", "help", "news", "add"]);
const TOOL_SECTIONS = new Set(["gameplanner", "trainingplans", "timers", "squad", "tournament"]);
const QUERY_TABS = new Set(["insights", "analysis", "ai", "training", "spotlight", "home", "results", "fixtures", "players", "stats", "plans", "videos", "board"]);
const REDIRECT_REASONS = new Set([
  "root_saved_team", "root_welcome", "protected_login", "auth_return", "auth_welcome",
  "demo", "legacy_ai", "team_canonical", "team_missing", "team_access", "unknown_route",
]);

/**
 * Maps a router location to a stable page key (ids/tokens stripped) plus the
 * team/club slug it relates to.
 */
export function toJourneyPage(pathname: string, search = ""): { page: string; team?: string } {
  const parts = pathname.split("/").filter(Boolean).map((part) => {
    try {
      return decodeURIComponent(part);
    } catch {
      return part;
    }
  });
  const requestedTab = new URLSearchParams(search).get("tab")?.toLowerCase();
  const tab = requestedTab && QUERY_TABS.has(requestedTab) ? requestedTab : null;
  const withTab = (page: string) => (tab ? `${page}:${sanitise(tab, 30)}` : page);
  const [rawRoot, second, third] = parts;
  const root = rawRoot?.toLowerCase();

  if (!root) return { page: "root" };

  switch (root) {
    case "team":
    case "club": {
      const section = third?.toLowerCase() || "home";
      const known = root === "team" ? TEAM_SECTIONS : CLUB_SECTIONS;
      return {
        page: withTab(`${root}/${root === "team" && section.startsWith("result-") ? "result" : known.has(section) ? section : "other"}`),
        ...(second ? { team: sanitise(second, 80) } : {}),
      };
    }
    case "pay":
    case "diary-attendance":
      return { page: root };
    default:
      if (root === "teams" || root === "clubs") return { page: withTab(second ? `${root}/${DIRECTORY_SECTIONS.has(second.toLowerCase()) ? second.toLowerCase() : "other"}` : root) };
      if (root === "tools") return { page: withTab(second ? `tools/${TOOL_SECTIONS.has(second.toLowerCase()) ? second.toLowerCase() : "other"}` : "tools") };
      if (root === "help") return { page: second === "privacy" ? "help/privacy" : "help" };
      return { page: ["login", "signup", "scouts", "demo"].includes(root) ? root : "unknown" };
  }
}

/** Router path shape only. Query values, fragments, IDs and result names are never stored. */
export function toJourneyRoute(pathname: string, search = ""): string {
  const { page } = toJourneyPage(pathname, search);
  const parts = pathname.split("/").filter(Boolean);
  if (parts[0]) parts[0] = parts[0].toLowerCase();
  if (parts[0] === "team" || parts[0] === "club") {
    const section = parts.length > 2 ? `/${page.split("/")[1].split(":")[0]}` : "";
    return `/${parts[0]}/:slug${section}${parts.length > 3 ? "/:detail" : ""}${page.includes(":") ? `?tab=${page.split(":")[1]}` : ""}`;
  }
  if (parts[0] === "pay") return "/pay/:token";
  if (parts[0] === "diary-attendance") return "/diary-attendance/:request";
  return page === "root" ? "/" : `/${page.replace(":", "?tab=")}`;
}

export const hasRecentJourneyNavigationSignal = () => performance.now() - lastNavigationSignal <= 1500;

const startSession = (uid: string, now: number) => {
  let id = `${now.toString(36)}${randomId(6)}`;
  let day = new Date(now).toISOString().slice(0, 10);
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    const stored = raw ? JSON.parse(raw) : null;
    if (stored?.uid === uid && typeof stored.id === "string" && /^[a-z0-9]{6,40}$/.test(stored.id) &&
        typeof stored.last === "number" && now >= stored.last && now - stored.last < SESSION_IDLE_MS &&
        typeof stored.day === "string" && /^\d{4}-\d{2}-\d{2}$/.test(stored.day)) {
      id = stored.id;
      day = stored.day;
    }
  } catch (error) {
    console.warn("[journey] session storage unavailable or invalid", error);
  }
  session = { uid, id, day, lastActivity: now, sequence: 0 };
};

const persistSession = () => {
  if (!session) return;
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({
      uid: session.uid, id: session.id, day: session.day, last: session.lastActivity,
    }));
  } catch (error) {
    console.warn("[journey] unable to persist session", error);
  }
};

const scheduleFlush = () => {
  if (queue.length >= MAX_QUEUE) {
    void flushJourney();
    return;
  }
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushJourney();
  }, FLUSH_DELAY_MS);
};

const enqueue = (kind: EventKind, name?: string, outcome?: "success" | "failure", team?: string) => {
  if (!lastPage || !session || disabled) return;
  const t = Date.now();
  const tick = performance.now();
  const elapsed = Math.max(0, tick - lastTick);
  const uncertain = visitOpen && (tick < lastTick || t < lastWall ||
    Math.abs((t - lastWall) - elapsed) > 2000 || elapsed > MAX_TIMING_GAP_MS);
  // A suspended timer is not evidence that the app stayed foregrounded.
  const d = visible && visitOpen && tick >= lastTick && elapsed <= MAX_TIMING_GAP_MS ? Math.round(elapsed) : 0;
  const a = visible && visitOpen && !uncertain ? Math.round(Math.max(0, Math.min(tick, activeUntil) - lastTick)) : 0;
  eventCounter = (eventCounter + 1) % 1296;
  const eventTeam = team ?? lastPage.team;
  queue.push({
    uid: lastPage.uid,
    day: session.day,
    sessionId: session.id,
    eventId: `${t.toString(36)}${eventCounter.toString(36).padStart(2, "0")}${randomId(3)}`,
    p: kind === "hidden" || kind === "exit" ? JOURNEY_EXIT_PAGE : lastPage.page,
    t,
    ...(eventTeam ? { tm: eventTeam } : {}),
    v: 2, k: kind, i: lastPage.visitId, q: ++session.sequence, d, a,
    ...(name ? { n: name } : {}),
    ...(outcome ? { o: outcome } : {}),
    ...(uncertain ? { u: true } : {}),
    ...(kind === "page" || kind === "visible" ? {
      ...(lastPage.route?.route ? { rt: lastPage.route.route } : {}),
      ...(lastPage.route?.previousRoute ? { pr: lastPage.route.previousRoute } : {}),
      ...(lastPage.route?.source ? { s: lastPage.route.source } : {}),
      ...(lastPage.route?.navigationType ? { nt: lastPage.route.navigationType } : {}),
      ...(lastPage.route?.redirect && REDIRECT_REASONS.has(lastPage.route.redirect) ? { rr: lastPage.route.redirect } : {}),
    } : {}),
  });
  lastTick = tick;
  lastWall = t;
  scheduleFlush();
};

const beginVisit = (kind: "page" | "visible", preserveActivity = false) => {
  if (!lastPage) return;
  lastPage.visitId = `${Date.now().toString(36)}${randomId(6)}`;
  lastTick = performance.now();
  lastWall = Date.now();
  if (!preserveActivity) activeUntil = 0;
  visitOpen = visible;
  enqueue(kind);
  if (lastPage.rendered) enqueue("rendered", undefined, undefined, lastPage.renderedTeam);
};

const ensureSession = (uid: string) => {
  const now = Date.now();
  if (session && session.uid === uid && now >= session.lastActivity && now - session.lastActivity < SESSION_IDLE_MS) return false;
  if (lastPage && session && visitOpen) enqueue("end");
  session = null;
  startSession(uid, now);
  persistSession();
  return true;
};

/** Records a page view for a signed-in user. Consecutive duplicates are ignored. */
export function recordJourneyPage(uid: string | null | undefined, pathname: string, search = "", route?: JourneyRouteContext): void {
  if (!uid || disabled) {
    if (lastPage && !disabled) recordJourneyExit();
    lastPage = null;
    session = null;
    return;
  }
  const { page, team } = toJourneyPage(pathname, search);
  const newSession = ensureSession(uid);
  if (!newSession && lastPage?.uid === uid && lastPage.page === page && lastPage.team === team &&
      lastPage.route?.route === route?.route) return;
  if (!newSession && lastPage) enqueue("end");
  lastPage = { uid, page, team, visitId: "", route };
  if (session) session.lastActivity = Date.now();
  persistSession();
  beginVisit("page", !newSession);
}

/** A committed destination shell is not proof of data loading or a successful business action. */
export function recordJourneyRendered(pathname: string, search = "", teamId?: string): void {
  if (!lastPage || disabled || lastPage.rendered || lastPage.route?.route !== toJourneyRoute(pathname, search)) return;
  lastPage.rendered = true;
  lastPage.renderedTeam = teamId;
  enqueue("rendered", undefined, undefined, teamId);
}

/** Marks the end of the current page view (app hidden / closed) and flushes. */
export function recordJourneyExit(): void {
  if (!lastPage || disabled) return;
  enqueue("exit");
  lastPage = null;
  activeUntil = 0;
  visitOpen = false;
  void flushJourney();
}

/** Re-records the current page after the app returns to the foreground. */
export function resumeJourney(uid: string | null | undefined, pathname: string, search = ""): void {
  if (!lastPage) visible = false;
  recordJourneyVisibility(true, uid, pathname, search);
}

export function recordJourneyVisibility(isVisible: boolean, uid?: string | null, pathname = "/", search = ""): void {
  if (visible === isVisible) return;
  if (!isVisible) {
    enqueue("hidden");
    visitOpen = false;
    visible = false;
    activeUntil = 0;
    void flushJourney();
  } else {
    visible = true;
    visitOpen = false;
    if (!uid || disabled) return;
    ensureSession(uid);
    const { page, team } = toJourneyPage(pathname, search);
    const rendered = lastPage?.uid === uid && lastPage.page === page && lastPage.team === team && lastPage.rendered;
    const renderedTeam = rendered ? lastPage?.renderedTeam : undefined;
    lastPage = { uid, page, team, visitId: "", rendered, renderedTeam, route: { route: toJourneyRoute(pathname, search), source: "entry" } };
    beginVisit("visible");
  }
}

function recordActivity(kind: "interaction" | "action", name: string, outcome?: "success" | "failure", team?: string): void {
  if (!lastPage || !visible || disabled) return;
  if (ensureSession(lastPage.uid)) {
    if (lastPage.route) lastPage.route = { route: lastPage.route.route, source: "entry" };
    beginVisit("page");
  }
  enqueue(kind, name, outcome, team ?? lastPage.renderedTeam);
  activeUntil = performance.now() + ENGAGEMENT_WINDOW_MS;
  if (session) session.lastActivity = Date.now();
  persistSession();
}

export function recordJourneyInteraction(kind: Interaction): void {
  if (kind === "click" || kind === "submit" || kind === "keyboard") lastNavigationSignal = performance.now();
  if (kind === "scroll") {
    if (performance.now() - lastScroll < 5000) return;
    lastScroll = performance.now();
  }
  recordActivity("interaction", kind);
}

/** Only call for successful, user-facing actions; never pass field values or labels. */
export function recordJourneyAction(name: string, outcome?: "success" | "failure", team?: string): void {
  recordActivity("action", sanitise(name, 60), outcome, team);
}

/** Writes all queued events. Failures are swallowed — journey logging is best-effort. */
export async function flushJourney(): Promise<void> {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (queue.length === 0 || disabled) {
    queue = [];
    return;
  }
  const batch = queue;
  queue = [];

  const groups = new Map<string, Record<string, EventData>>();
  batch.forEach(({ uid, day, sessionId, eventId, ...event }) => {
    const path = `journeys/${day}/${uid}/${sessionId}`;
    const group = groups.get(path) ?? {};
    group[eventId] = event;
    groups.set(path, group);
  });

  await Promise.all([...groups.entries()].map(async ([path, data]) => {
    try {
      if (shouldUseNativeDatabase()) {
        await nativeUpdate(path, data);
      } else {
        await update(ref(database, path), data);
      }
    } catch (error) {
      if (isPermissionDenied(error)) {
        if (!disabled) console.warn("[journey] permission denied — journey logging paused until reload");
        disabled = true;
        queue = [];
        lastPage = null;
        return;
      }
      console.warn("[journey] flush failed", error);
    }
  }));
}

/** Test helper: resets in-memory state. */
export function resetJourneyTrackingForTests(): void {
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = null;
  queue = [];
  lastPage = null;
  eventCounter = 0;
  disabled = false;
  session = null;
  visible = true;
  visitOpen = false;
  lastTick = lastWall = activeUntil = 0;
  lastScroll = -Infinity;
  lastNavigationSignal = -Infinity;
}
